import { istLeererBlocktext, type Block } from "./blockModell";

/**
 * DIE NOTIZEN SIND EIN TEXTFELD (PO 18.09.2026: „Die Notizen müssen ein Block
 * sein"; 20.09.2026 bestätigt) — REINE Ableitung, bewusst ohne React und ohne
 * DOM, damit der node-Runner sie direkt prüft.
 *
 * ══ EIN FELD, VIELE ZEILEN ═════════════════════════════════════════════════
 * Sichtbar ist EINE Fläche mit EINEM Cursor: Pfeiltasten, Markierung und
 * Rücktaste laufen über alle Absätze hinweg. Gespeichert wird weiterhin EINE
 * Notizzeile je ABSCHNITT — an der einzelnen Zeile hängen Archiv, Suche,
 * Erwähnungen, Kommentare und die Frage „wer hat das geschrieben".
 *
 * Verworfen wurde „ein Dokument je Arbeitsstrang": es bräuchte eine Umwandlung
 * des gesamten Bestands, Verfasser und Zeit stünden nur noch als
 * überschreibbare Attribute im HTML, und zwei gleichzeitig Schreibende
 * überschrieben einander das ganze Dokument statt je ihren Abschnitt.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ WAS EIN ABSCHNITT IST ══════════════════════════════════════════════════
 * Jedes Kind der obersten Ebene des Feldes (`<p>`, `<div>`, `<ul>`, …) trägt
 * `data-nk` — den Schlüssel seiner Notizzeile (`quelle:id`, siehe
 * `notizSchluessel`). AUFEINANDERFOLGENDE Kinder mit demselben Schlüssel sind
 * EIN Abschnitt. Das oberste Kind trägt {@link NEU} und ist die Schreibstelle.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Der Schlüssel der Schreibstelle — was hier steht, wird eine neue Notiz. */
export const NEU = "neu";

/** Ein Abschnitt des Feldes: eine Notizzeile, ein oder mehrere Absätze. */
export type Abschnitt = {
    /** `quelle:id` der Notizzeile, oder {@link NEU}. */
    key: string;
    /** Der gespeicherte Inhalt — HTML, wenn `istHtml`, sonst Klartext. */
    html: string;
    istHtml: boolean;
    /** Verfasser (Benutzerschlüssel) — er steht als Kürzel am Rand. */
    autor: string;
    /** Alle, die geschrieben haben; der erste ist der Verfasser. */
    bearbeiter: string[];
    angelegt: Date;
    zuletzt: Date;
    /** Handschrift vom reMarkable: steht im Feld, lässt sich aber nicht bearbeiten. */
    gesperrt?: true;
};

/**
 * Die Abschnitte eines Arbeitsstrangs in ANZEIGEREIHENFOLGE — neueste zuerst
 * (PO 14.09.2026: „Notizen-Verlauf umdrehen"), die Schreibstelle steht darüber.
 *
 * `bloeckeAus` liefert älteste zuerst; gedreht wird nur die Anzeige, das Modell
 * bleibt wie es ist. Eine von Hand gesetzte Reihenfolge (`sortIndex` aus der
 * Zeit vor dem 20.09.2026) wird dabei respektiert — neu verschieben lässt sich
 * nichts mehr, das Hoch-Runter ist am 18.09.2026 entfallen.
 */
export function abschnitteAus(bloecke: Block[]): Abschnitt[] {
    return bloecke
        .map((b) => ({
            key: b.key,
            html: b.html,
            istHtml: b.istHtml,
            autor: b.autor,
            bearbeiter: b.bearbeiter.length ? b.bearbeiter : [b.autor],
            angelegt: b.angelegt,
            zuletzt: b.zuletzt,
            ...(b.gesperrt ? { gesperrt: true as const } : {}),
        }))
        .reverse();
}

/**
 * Wem gehört jedes Kind des Feldes?
 *
 * Der Browser hält `data-nk` nicht von allein sauber: Enter kopiert die
 * Attribute des Absatzes (dann stimmt es), Einfügen aus Word bringt Kinder
 * OHNE Schlüssel, und wer einen Absatz von oben nach unten zieht, trägt seinen
 * Schlüssel mitten in einen fremden Abschnitt.
 *
 * EINE REGEL für alle drei Fälle: Ein Kind gehört zu dem Abschnitt DARÜBER,
 * außer sein Schlüssel setzt den direkt darüber fort oder er kommt hier zum
 * ersten Mal vor. Ganz oben gibt es nichts darüber — dort ist es neuer Text.
 *
 * Damit bleibt die Reihenfolge im Feld immer auch die Reihenfolge der
 * gespeicherten Zeilen: Ein zweites, nicht anschließendes Vorkommen desselben
 * Schlüssels würde sonst beim nächsten Laden nach oben springen, weil es dort
 * mit seinem Abschnitt zusammengezogen wird.
 */
export function zuordnen(keys: Array<string | null | undefined>): string[] {
    const vergeben = new Set<string>();
    const out: string[] = [];
    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        const davor = i > 0 ? out[i - 1]! : null;
        if (k && (k === davor || !vergeben.has(k))) {
            out.push(k);
            vergeben.add(k);
        } else {
            out.push(davor ?? NEU);
        }
    }
    return out;
}

/** Ein aus dem Feld gelesener Abschnitt: Schlüssel und der Stand seines HTML. */
export type AbschnittStand = { key: string; html: string };

/**
 * Was gespeichert werden muss. `vorher` ist der zuletzt bekannte Stand je
 * Schlüssel, `nachher` das, was jetzt im Feld steht.
 */
export type Aenderungen = {
    /**
     * Was an der Schreibstelle steht; `""` = nichts Neues. Mehrere Absätze
     * dort werden EINE Notiz — sie sind ein Gedanke, den jemand gerade
     * schreibt, nicht mehrere.
     */
    neu: string;
    /** Abschnitte, deren Inhalt sich geändert hat. */
    geaendert: AbschnittStand[];
    /**
     * Schlüssel, die es nicht mehr gibt oder die leergeräumt wurden —
     * „leergeräumt heißt gelöscht" (PO 13.09.2026).
     */
    geloescht: string[];
};

/**
 * Der Vergleich Stand gegen Feld.
 *
 * Er entscheidet NICHT, wann gespeichert wird (das tut die Komponente: nach
 * kurzer Ruhe, Löschungen erst beim Verlassen) — nur WAS sich geändert hat.
 * Ein Schlüssel, der in `vorher` gar nicht steht und auch nicht {@link NEU}
 * ist, wird übergangen: er kann nur aus einer fremden Zeile stammen, die
 * inzwischen ein anderer gelöscht hat.
 */
export function aenderungen(
    vorher: Map<string, string>,
    nachher: AbschnittStand[],
): Aenderungen {
    const neuStuecke: string[] = [];
    const geaendert: AbschnittStand[] = [];
    const gesehen = new Set<string>();

    for (const a of nachher) {
        if (a.key === NEU) {
            if (!istLeererBlocktext(a.html)) neuStuecke.push(a.html);
            continue;
        }
        if (!vorher.has(a.key)) continue;
        gesehen.add(a.key);
        if (istLeererBlocktext(a.html)) continue; // fällt unten unter „gelöscht"
        if (a.html !== vorher.get(a.key)) geaendert.push(a);
    }

    const geloescht: string[] = [];
    for (const key of vorher.keys()) {
        const steht = nachher.find((a) => a.key === key);
        if (!gesehen.has(key) || !steht || istLeererBlocktext(steht.html)) {
            geloescht.push(key);
        }
    }

    return { neu: neuStuecke.join(""), geaendert, geloescht };
}

/**
 * Was beim Überfahren des Kürzels stehen soll: „Sandra Kessler · heute, 10:12",
 * mit Nachsatz, wenn jemand anderes darin weitergeschrieben hat.
 *
 * „Es reicht, wenn das Kürzel da steht und dann die Uhrzeit, das Datum … oder
 * ich gehe einfach da drüber" (PO 18.09.2026) — der Name kostet damit keine
 * Zeile, und verloren geht er trotzdem nicht.
 */
export function hoverTitel(
    a: Pick<Abschnitt, "autor" | "bearbeiter" | "angelegt" | "zuletzt">,
    nameVon: (userId: string) => string,
    jetzt: Date = new Date(),
): string {
    const fremde = a.bearbeiter.filter((b) => b !== a.autor);
    const kopf = `${nameVon(a.autor)} · ${zeitpunkt(a.angelegt, jetzt)}`;
    if (!fremde.length) return kopf;
    return `${kopf} · bearbeitet von ${fremde.map(nameVon).join(", ")}`;
}

/** „heute, 10:12" / „Do., 10.09., 15:40" / „10.09.2025, 15:40". */
export function zeitpunkt(d: Date, jetzt: Date = new Date()): string {
    if (!d || Number.isNaN(d.getTime()) || d.getTime() === 0) return "";
    const uhr = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const selberTag =
        d.getFullYear() === jetzt.getFullYear() &&
        d.getMonth() === jetzt.getMonth() &&
        d.getDate() === jetzt.getDate();
    if (selberTag) return `heute, ${uhr}`;
    if (d.getFullYear() === jetzt.getFullYear()) {
        const tag = d.toLocaleDateString("de-DE", {
            weekday: "short",
            day: "2-digit",
            month: "2-digit",
        });
        return `${tag}, ${uhr}`;
    }
    return `${d.toLocaleDateString("de-DE")}, ${uhr}`;
}
